import { create } from "zustand";

import { resetTranslationAssist } from "./translate";

/**
 * Восемь уровней зума по аналогии с iOS Calendar: от часовой шкалы одного
 * дня до общего обзора всех годов. Все уровни делятся на «полосовые»
 * (одна горизонтальная лента ячеек на участника — hour/day/week) и
 * «сеточные» (классическая календарная сетка — month/threeMonths/sixMonths/year/allYears).
 *
 * GHG6 CL0: уровень `twoWeeks` удалён в рамках подготовки к новому
 * TimelineView. Дефолт — `week`. Если позже захочется «произвольный
 * диапазон», добавим его отдельным presetom внутри TimelineView (CL12).
 */
export type ZoomLevel =
  | "hour"
  | "day"
  | "week"
  | "month"
  | "threeMonths"
  | "sixMonths"
  | "year"
  | "allYears";

export const ZOOM_ORDER: ZoomLevel[] = [
  "allYears",
  "year",
  "sixMonths",
  "threeMonths",
  "month",
  "week",
  "day",
  "hour",
];

export const ZOOM_LABELS: Record<ZoomLevel, string> = {
  hour: "Часы",
  day: "День",
  week: "Неделя",
  month: "Месяц",
  threeMonths: "3 мес",
  sixMonths: "Полгода",
  year: "Год",
  allYears: "Все годы",
};

export function isStripZoom(z: ZoomLevel): boolean {
  return z === "day" || z === "week";
}

export function isMonthGridZoom(z: ZoomLevel): boolean {
  return z === "month" || z === "threeMonths" || z === "sixMonths";
}

// GHG8 P4.1.d: вкладка "leaderboard" (Топы) заменена на "profile" — топы
// переехали внутрь профиля вместе с историей и настройками приветствия.
// Э21: вкладки пересобраны. Первой идёт "feed" — лента активности, туда
// приложение открывается по умолчанию (в софт-режимах там вся жизнь чата).
// "meetings" теперь ОБЪЕДИНЯЕТ встречи и опросы (вкладки "polls" больше нет:
// ими почти не пользовались, держим один раздел с переключателем внутри).
// SemiTaskBot: рабочие вкладки встали перед сервисными. Сотрудник открывает
// приложение, чтобы закрыть смену и посмотреть поручения, поэтому "tasks" и
// "checklist" идут первыми, "feed" остаётся разделом событий (с новой
// ролевой видимостью), "calendar" и "meetings" сохраняются как есть.
// "issues" — журнал операционных проблем. В нижнюю полосу не встаёт (там уже
// пять-шесть подписей), но и не спрятан: вход из «Мои задачи» и из профиля.
export type Tab =
  | "tasks"
  | "checklist"
  /** Табель смен: неделя своего отдела, управляющему — все отделы. */
  | "shifts"
  | "feed"
  | "links"
  /** Материалы смены: график, меню, методички (снимок Google Sheets). */
  | "materials"
  | "calendar"
  | "meetings"
  | "team"
  | "issues"
  | "handovers"
  /** База стандартов и тесты по инструкциям. */
  | "standards"
  /** «Спросить»: один поиск по стандартам и пунктам чек-листов. */
  | "ask"
  /** Очередь приёмки работ руководителя. */
  | "review"
  /** Отчёт смены: срез на дату, стандарты и выгрузка CSV. */
  | "reports"
  | "profile"
  | "admin";

/**
 * Зона приложения: рабочий день сотрудника или кабинет управления.
 *
 * Это режим всего экрана, а не набор вкладок роли. У сотрудника зона одна и
 * верхних вкладок нет вовсе; у руководителя их две, и вторая — другой кабинет
 * (мониторинг, люди, поручения, документы, показатели, графики), а не седьмая
 * кнопка в нижней полосе.
 *
 * Не сохраняется между запусками осознанно: приложение всегда открывается
 * зоной сотрудника. Иначе руководитель, закрывший приложение в кабинете,
 * следующим утром не нашёл бы свою смену — а именно с неё начинается день.
 */
export type AppMode = "staff" | "manage";

interface UIState {
  tab: Tab;
  setTab: (t: Tab) => void;

  /** Зона экрана: сотрудник или управление. */
  mode: AppMode;
  setMode: (m: AppMode) => void;

  zoom: ZoomLevel;
  setZoom: (z: ZoomLevel) => void;
  zoomIn: () => void;
  zoomOut: () => void;

  /** Дата, вокруг которой строится текущий вид. */
  anchorDate: Date;
  setAnchorDate: (d: Date) => void;
  /** Сдвиг anchor на N единиц текущего zoom-уровня (вперёд/назад). */
  shiftAnchor: (direction: 1 | -1) => void;
  /** Прыжок к сегодня. */
  goToday: () => void;

  editingRangeId: number | "new" | null;
  setEditingRangeId: (v: number | "new" | null) => void;

  showAutoPickSheet: boolean;
  setShowAutoPickSheet: (v: boolean) => void;

  showLoserSheet: boolean;
  setShowLoserSheet: (v: boolean) => void;

  showPollSheet: boolean;
  setShowPollSheet: (v: boolean) => void;

  /**
   * GHG6 BD2: поповер «🎂 поздравить / назначить встречу». Хранит, чьё ДР
   * и на какую дату открыто. Закрывается через setBirthdayPopover(null).
   */
  birthdayPopover: { userId: number; date: string; displayName: string } | null;
  setBirthdayPopover: (
    v: { userId: number; date: string; displayName: string } | null,
  ) => void;

  /**
   * GHG7 P0.2.e: попап с причиной ролла по клику на корону 👑.
   * userId — кому корона, date — день (YYYY-MM-DD), displayName — имя для
   * заголовка. Закрывается через setLoserReasonPopover(null).
   */
  loserReasonPopover: {
    userId: number;
    date: string;
    displayName: string;
  } | null;
  setLoserReasonPopover: (
    v: { userId: number; date: string; displayName: string } | null,
  ) => void;

  /**
   * GHG8 P2.1.c: попап-история звания «🪱 Червь-пидор» по клику на бейдж под
   * аватаркой. Звание переходящее и одно на всех — храним просто флаг
   * открытия, без per-user полей.
   */
  showWormHistory: boolean;
  setShowWormHistory: (v: boolean) => void;

  /**
   * Э19: гостевой профиль участника (клик по аватарке на календаре). Хранит
   * ВНУТРЕННИЙ id юзера; null — оверлей закрыт. Рендерится поверх вкладок,
   * поэтому настройки своего профиля и пояснения туда не попадают.
   */
  guestUserId: number | null;
  openGuest: (userId: number) => void;
  closeGuest: () => void;

  /**
   * GHG6 BD2: дата, с которой надо открыть PollSheet при «Назначить встречу»
   * из поповера. PollSheet читает её при монтаже и кладёт в первый вариант.
   */
  pollSheetPresetDate: string | null;
  setPollSheetPresetDate: (date: string | null) => void;

  /**
   * GHG8 P2.4.c: предзаполненный вопрос опроса при открытии PollSheet из
   * ДР-поповера («Собираемся на ДР {имя}?»). Очищается вместе с presetDate.
   */
  pollSheetPresetQuestion: string | null;
  setPollSheetPresetQuestion: (q: string | null) => void;

  /**
   * GHG6 P3 CL5: пользовательский cellWidth для TimelineView. null = «авто»
   * (TimelineView сам считает по ResizeObserver — CL6.a). Когда пользователь
   * двинул слайдер зума или нажал пресет «День»/«Неделя»/«Месяц», сюда
   * кладётся фиксированное значение в px и TimelineView читает его.
   */
  timelineCellWidth: number | null;
  setTimelineCellWidth: (w: number | null) => void;

  /**
   * GHG11(4): якорь, к которому надо доскроллить и, если нужно, раскрыть
   * соответствующий блок при переходе из анонса фичи. Ставится перед сменой
   * вкладки, потребляется целевым экраном (напр. лента раскрывает «Действия»)
   * и сразу очищается.
   */
  feedAnchor: string | null;
  setFeedAnchor: (a: string | null) => void;

  /**
   * GHG11(14/16): личные уведомления — нижний шит (как фильтр участников),
   * а не панель в потоке ленты.
   *
   * GHG11(13) свалил фильтры, действия и уведомления в одну панель под
   * колокольчиком (фильтры читались как часть уведомлений), GHG11(14) развёл их,
   * но оставил ту же механику: колокольчик-тумблер выдвигал и вдвигал панель,
   * врезанную между шапкой и лентой. Теперь колокольчик только ОТКРЫВАЕТ шит;
   * сворачивания по нему нет — закрытие там, где его ищут в мобильном: подложка.
   */
  showNotificationsSheet: boolean;
  setShowNotificationsSheet: (v: boolean) => void;

  /**
   * GHG11(17): шит плеера. Живёт на месте, где раньше был колокольчик: выбор
   * подборки недели / «всё подряд» / «случайно» / «только лайкнутые».
   */
  showPlayerSheet: boolean;
  setShowPlayerSheet: (v: boolean) => void;

  /**
   * GHG11(4): подраздел админки, который надо открыть сразу при переходе из
   * анонса фичи ("loser"/"chukhan"/…). Потребляется `AdminScreen` и
   * очищается после применения.
   */
  pendingAdminSection: string | null;
  setPendingAdminSection: (s: string | null) => void;

  /**
   * Какой раздел кабинета открыт. Лежит в сторе, а не в `AdminScreen`, потому
   * что переключать его теперь может не только сам кабинет: нижняя полоса
   * зоны управления (02.11.2026) ведёт сразу в важные разделы, и ей нужно
   * и подсвечивать текущий, и слышать клик. `"root"` — сам список разделов.
   */
  adminSection: string;
  setAdminSection: (s: string) => void;
}

function shiftDateByZoom(d: Date, z: ZoomLevel, dir: 1 | -1): Date {
  const x = new Date(d);
  switch (z) {
    case "hour":
    case "day":
      x.setDate(x.getDate() + dir);
      break;
    case "week":
      x.setDate(x.getDate() + dir * 7);
      break;
    case "month":
      x.setMonth(x.getMonth() + dir);
      break;
    case "threeMonths":
      x.setMonth(x.getMonth() + dir * 3);
      break;
    case "sixMonths":
      x.setMonth(x.getMonth() + dir * 6);
      break;
    case "year":
      x.setFullYear(x.getFullYear() + dir);
      break;
    case "allYears":
      x.setFullYear(x.getFullYear() + dir * 10);
      break;
  }
  return x;
}

export const useUI = create<UIState>((set, get) => ({
  // SemiTaskBot (раунд 5): приложение открывается на «Событиях» — это общий
  // экран смены, где видно, что происходит у коллег. Свои задачи и чек-лист
  // остаются рядом: они вторые и третьи в нижней полосе.
  tab: "feed",
  mode: "staff",
  setMode: (mode) => {
    if (get().mode === mode) return;
    // Подмена перевода живёт до ухода с экрана (план, 11): при смене зоны
    // экран другой, и чужой переведённый текст в нём был бы сюрпризом.
    resetTranslationAssist();
    set({ mode });
  },
  setTab: (tab) => {
    // Перевод данных включается кнопкой на конкретном блоке и живёт до ухода с
    // экрана (план, 11). Вкладки здесь не размонтируются, поэтому сброс идёт
    // событием: без него человек вернулся бы на экран с подменённым текстом,
    // о котором уже забыл. Смена вкладки — ровно тот момент, когда он начал
    // читать другое.
    resetTranslationAssist();
    set({ tab });
  },

  zoom: "week",
  setZoom: (zoom) => set({ zoom }),
  zoomIn: () => {
    const cur = get().zoom;
    const i = ZOOM_ORDER.indexOf(cur);
    if (i < ZOOM_ORDER.length - 1) set({ zoom: ZOOM_ORDER[i + 1] });
  },
  zoomOut: () => {
    const cur = get().zoom;
    const i = ZOOM_ORDER.indexOf(cur);
    if (i > 0) set({ zoom: ZOOM_ORDER[i - 1] });
  },

  anchorDate: new Date(),
  setAnchorDate: (anchorDate) => set({ anchorDate }),
  shiftAnchor: (dir) =>
    set({ anchorDate: shiftDateByZoom(get().anchorDate, get().zoom, dir) }),
  goToday: () => set({ anchorDate: new Date() }),

  editingRangeId: null,
  setEditingRangeId: (editingRangeId) => set({ editingRangeId }),

  showAutoPickSheet: false,
  setShowAutoPickSheet: (v) => set({ showAutoPickSheet: v }),

  showLoserSheet: false,
  setShowLoserSheet: (v) => set({ showLoserSheet: v }),

  showPollSheet: false,
  setShowPollSheet: (v) => set({ showPollSheet: v }),

  birthdayPopover: null,
  setBirthdayPopover: (birthdayPopover) => set({ birthdayPopover }),

  loserReasonPopover: null,
  setLoserReasonPopover: (loserReasonPopover) => set({ loserReasonPopover }),

  showWormHistory: false,
  setShowWormHistory: (showWormHistory) => set({ showWormHistory }),

  guestUserId: null,
  openGuest: (guestUserId) => set({ guestUserId }),
  closeGuest: () => set({ guestUserId: null }),

  pollSheetPresetDate: null,
  setPollSheetPresetDate: (pollSheetPresetDate) => set({ pollSheetPresetDate }),

  pollSheetPresetQuestion: null,
  setPollSheetPresetQuestion: (pollSheetPresetQuestion) =>
    set({ pollSheetPresetQuestion }),

  timelineCellWidth: null,
  setTimelineCellWidth: (timelineCellWidth) => set({ timelineCellWidth }),

  feedAnchor: null,
  setFeedAnchor: (feedAnchor) => set({ feedAnchor }),

  showNotificationsSheet: false,
  setShowNotificationsSheet: (showNotificationsSheet) =>
    set({ showNotificationsSheet }),

  showPlayerSheet: false,
  setShowPlayerSheet: (showPlayerSheet) => set({ showPlayerSheet }),

  pendingAdminSection: null,
  setPendingAdminSection: (pendingAdminSection) => set({ pendingAdminSection }),

  adminSection: "root",
  setAdminSection: (adminSection) => set({ adminSection }),
}));
